import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  GitBranch,
  Headphones,
  LayoutDashboard,
  HelpCircle,
  MessageCircle,
  MessagesSquare,
  Bot,
  Bell,
  FileText,
  Users,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  Crown,
  Folder,
  Shield,
  Menu,
  X,
  Home,
  Briefcase,
  UserPlus,
  Search,
  Clock,
  Sparkles,
  Target,
  DollarSign,
  Package,
  BookOpen,
  Radio,
  Activity,
  CreditCard,
  CalendarDays,
  Wrench,
  TrendingUp,
  ClipboardList,
  ListChecks,
  AlertTriangle,
  Trash2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState({
    principal: true,
    clientes: false,
    ventas: false,
    analiticas: false,
    herramientas: false,
    config: false,
    recursos: false
  });
  const { user, logout, isPremium, hasFeature } = useAuth();
  const location = useLocation();
  
  const isAdmin = user?.role === "admin" || user?.role === "legal";
  const isVendedor = user?.role === "vendedor";
  // REMOVED: isOficial - role eliminated, merged into admin
  const isStaff = isAdmin || isVendedor;

  // Fetch unread messages count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        
        if (isAdmin) {
          // Admin: get total unread from clients
          const res = await axios.get(`${API}/support-chat/admin/unread-count`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUnreadMessages(res.data.unread_count || 0);
        } else if (user?.role === "client") {
          // Client: get unread from admin
          const res = await axios.get(`${API}/support-chat/my-chat`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUnreadMessages(res.data.unread_count || 0);
        }
      } catch (error) {
        console.error("Error fetching unread count:", error);
      }
    };

    fetchUnreadCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [isAdmin, user?.role]);

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setIsOpen(true);
      }
    };
    
    window.addEventListener("resize", handleResize);
    handleResize();
    
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (isMobile) {
      setIsOpen(false);
    }
  }, [location.pathname, isMobile]);

  const isActive = (path) => {
    if (path === "/admin" && location.pathname.startsWith("/admin")) {
      return location.pathname === "/admin";
    }
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  // Check if user is on autoservicio (standard) plan
  const isAutoservicio = user?.plan_type === "estandar" || user?.plan_type === "standard" || user?.plan_type === "prueba";
  const isPremiumOrVip = user?.plan_type === "premium" || user?.plan_type === "vip";

  // Menu items for regular users - Soporte humano only for Premium/VIP
  const userMenuItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", description: "Tu Camino al Éxito" },
    { path: "/documents", icon: Folder, label: "Documentos", description: "Historial de archivos" },
    { path: "/interview-questions", icon: HelpCircle, label: "Preguntas", description: "Banco de entrevista" },
    // Soporte humano solo para Premium/VIP
    !isAutoservicio && { path: "/soporte", icon: Headphones, label: "Soporte", description: "Chat con soporte", highlight: true, badge: unreadMessages > 0 ? unreadMessages : null },
    // Autoservicio: Revisión automática para plan estándar
    isAutoservicio && { path: "/autorevision", icon: ClipboardList, label: "Revisión", description: "Revisión automática", highlight: user?.plan_type === "estandar" },
    { path: "/simulacion-ia", icon: Bot, label: "Simulación IA", description: "Practica tu entrevista", premium: true },
    { path: "/help", icon: MessageCircle, label: "Ayuda IA", description: "Asistente 24/7" },
    { path: "/notifications", icon: Bell, label: "Notificaciones", description: "Alertas y avisos" },
    // VIP Partner referral dashboard
    user?.is_vip_partner && { path: "/mis-referidos", icon: Users, label: "Mis Referidos", description: "Gana comisiones", highlight: true },
  ].filter(Boolean); // Remove falsy values

  // Premium items
  const premiumItems = [
    { path: "/interview-simulator", icon: Bot, label: "Simulador", description: "Practica tu entrevista", premium: true },
  ];

  // Admin menu items - Simplified and organized (March 2026)
  const adminMenuCategories = [
    {
      id: 'principal',
      label: 'Principal',
      icon: Home,
      items: [
        { path: "/admin", icon: LayoutDashboard, label: "Centro de Comando", description: "Vista general" },
        { path: "/admin/clients/new", icon: UserPlus, label: "Nuevo Cliente", description: "Crear cliente", highlight: true },
      ]
    },
    {
      id: 'clientes',
      label: 'Clientes',
      icon: Users,
      items: [
        { path: "/admin/cases", icon: Folder, label: "Casos", description: "Todos los casos" },
        { path: "/admin/standard-queue", icon: ListChecks, label: "Cola Standard", description: "Autoservicio", highlight: true },
        { path: "/admin/clients/search", icon: Search, label: "Buscar Clientes", description: "Filtros avanzados" },
        { path: "/admin/ds160-pendientes", icon: ClipboardList, label: "DS-160 Pendientes", description: "Revisar respuestas" },
      ]
    },
    {
      id: 'pagos',
      label: 'Pagos',
      icon: CreditCard,
      items: [
        { path: "/admin/pagos-metricas", icon: CreditCard, label: "Métricas Pagos", description: "Ingresos y flujo", highlight: true },
        { path: "/admin/pagos-pendientes", icon: AlertTriangle, label: "Pagos Pendientes", description: "Verificar pagos", highlight: true },
        { path: "/admin/referidos", icon: Users, label: "Comisiones Referidos", description: "VIP Partners", highlight: true },
      ]
    },
    {
      id: 'herramientas',
      label: 'Herramientas',
      icon: Wrench,
      items: [
        { path: "/admin/soporte", icon: MessagesSquare, label: "Centro Soporte", description: "Chat con clientes", highlight: true, badge: unreadMessages > 0 ? unreadMessages : null },
        { path: "/admin/simulaciones", icon: Bot, label: "Simulacros IA", description: "Práctica de entrevista" },
        { path: "/admin/calendario", icon: CalendarDays, label: "Calendario", description: "Agenda de citas" },
      ]
    },
    {
      id: 'config',
      label: 'Configuración',
      icon: Settings,
      items: [
        { path: "/admin/products", icon: Package, label: "Productos", description: "Planes y precios" },
        { path: "/admin/reminders", icon: Bell, label: "Recordatorios", description: "Mensajes automáticos" },
        { path: "/admin/documents", icon: FileText, label: "Documentos", description: "Archivos de clientes" },
        { path: "/admin/limpiar-duplicados", icon: Trash2, label: "Limpiar Duplicados", description: "Mantenimiento" },
      ]
    },
  ];

  // Keep old flat array for backwards compatibility (not used anymore for admin)
  const adminMenuItems = adminMenuCategories.flatMap(cat => cat.items);

  // REMOVED: vendedorMenuItems - vendedor role eliminated (March 2026)
  // Vendors are now VIP Partners (clients with special discounts)
  const vendedorMenuItems = [];

  // REMOVED: oficialMenuItems - role eliminated

  const NavItem = ({ item }) => {
    const active = isActive(item.path);
    const Icon = item.icon;
    const isLocked = item.premium && !isPremium();
    
    const handleClick = (e) => {
      if (isLocked) {
        e.preventDefault();
        // Could show a toast here if needed
      }
    };
    
    return (
      <Link to={isLocked ? "#" : item.path} onClick={handleClick}>
        <motion.div
          whileHover={{ x: isLocked ? 0 : 4 }}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
            active
              ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/25"
              : isLocked
              ? "text-navy-400 bg-navy-50 opacity-60 cursor-not-allowed"
              : item.highlight
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
              : "text-navy-600 hover:bg-navy-100"
          }`}
        >
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            active 
              ? "bg-white/20" 
              : isLocked
              ? "bg-navy-100"
              : item.highlight
              ? "bg-emerald-100"
              : "bg-navy-100 group-hover:bg-navy-200"
          }`}>
            <Icon className={`w-5 h-5 ${
              active 
                ? "text-white" 
                : isLocked
                ? "text-navy-400"
                : item.highlight
                ? "text-emerald-600"
                : "text-navy-500"
            }`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`font-medium text-sm ${
              active 
                ? "text-white" 
                : isLocked
                ? "text-navy-500"
                : item.highlight
                ? "text-emerald-800"
                : "text-navy-900"
            }`}>
              {item.label}
            </div>
            <div className={`text-xs truncate ${
              active 
                ? "text-white/70" 
                : isLocked
                ? "text-navy-400"
                : item.highlight
                ? "text-emerald-600"
                : "text-navy-400"
            }`}>
              {isLocked ? "Plan Premium requerido" : item.description}
            </div>
          </div>
          {item.badge && (
            <span className="min-w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1.5 animate-pulse">
              {typeof item.badge === 'number' ? item.badge : '!'}
            </span>
          )}
          {isLocked && (
            <Crown className="w-4 h-4 text-amber-500" />
          )}
        </motion.div>
      </Link>
    );
  };

  return (
    <>
      {/* Toggle Button - Always visible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed top-20 z-50 p-2 bg-white rounded-r-xl shadow-lg border border-l-0 border-navy-200 hover:bg-navy-50 transition-all duration-300 ${
          isOpen ? "left-64" : "left-0"
        }`}
        style={{ transition: "left 0.3s ease" }}
      >
        {isOpen ? (
          <ChevronLeft className="w-5 h-5 text-navy-600" />
        ) : (
          <Menu className="w-5 h-5 text-navy-600" />
        )}
      </button>

      {/* Overlay for mobile */}
      <AnimatePresence>
        {isOpen && isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed top-0 left-0 h-full w-64 bg-white border-r border-navy-100 z-40 shadow-xl overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="p-4 border-b border-navy-100">
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <GitBranch className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="font-heading text-xl font-bold text-navy-900">Divídelo</span>
                  <p className="text-xs text-navy-400">Plataforma Migratoria</p>
                </div>
              </Link>
            </div>

            {/* User Info */}
            <div className="p-4 border-b border-navy-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold">{user?.name?.[0]}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-navy-900 text-sm truncate">{user?.name}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-navy-400 truncate">{user?.email}</span>
                  </div>
                </div>
                {isPremium() && (
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
                    PRO
                  </span>
                )}
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto p-4 space-y-2 scrollbar-thin">
              {/* Client Main Section - Only for clients */}
              {!isStaff && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-navy-400 uppercase tracking-wider mb-2 px-4">
                    Principal
                  </p>
                  {userMenuItems.map((item) => (
                    <NavItem key={item.path} item={item} />
                  ))}
                </div>
              )}

              {/* Client Premium Section - Only for clients */}
              {!isStaff && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-navy-400 uppercase tracking-wider mb-2 px-4">
                    Premium
                  </p>
                  {premiumItems.map((item) => (
                    <NavItem key={item.path} item={item} />
                  ))}
                </div>
              )}

              {/* Vendedor Section */}
              {isVendedor && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2 px-4 flex items-center gap-2">
                    <Briefcase className="w-3 h-3" />
                    Agente Vendedor
                  </p>
                  {vendedorMenuItems.map((item) => (
                    <NavItem key={item.path} item={item} />
                  ))}
                </div>
              )}

              {/* Oficial Section - ELIMINADO: Ahora Admin hace todo */}
              {/* Los oficiales ya no existen como rol separado */}

              {/* Admin Section - With collapsible categories */}
              {isAdmin && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2 px-4 flex items-center gap-2">
                    <Shield className="w-3 h-3" />
                    Administración
                  </p>
                  
                  {adminMenuCategories.map((category) => {
                    const CategoryIcon = category.icon;
                    const isExpanded = expandedCategories[category.id];
                    const hasActiveItem = category.items.some(item => isActive(item.path));
                    
                    return (
                      <div key={category.id} className="mb-1">
                        {/* Category Header */}
                        <button
                          onClick={() => toggleCategory(category.id)}
                          className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-left transition-all duration-200 ${
                            hasActiveItem 
                              ? "bg-emerald-50 text-emerald-700" 
                              : "text-navy-600 hover:bg-navy-50"
                          }`}
                        >
                          <CategoryIcon className={`w-4 h-4 ${hasActiveItem ? "text-emerald-600" : "text-navy-400"}`} />
                          <span className="flex-1 font-medium text-sm">{category.label}</span>
                          <ChevronDown 
                            className={`w-4 h-4 transition-transform duration-200 ${
                              isExpanded ? "rotate-180" : ""
                            } ${hasActiveItem ? "text-emerald-600" : "text-navy-400"}`} 
                          />
                        </button>
                        
                        {/* Category Items */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="ml-2 pl-2 border-l-2 border-navy-100 mt-1 space-y-1">
                                {category.items.map((item) => {
                                  const Icon = item.icon;
                                  const active = isActive(item.path);
                                  return (
                                    <Link key={item.path} to={item.path}>
                                      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                                        active
                                          ? "bg-emerald-500 text-white"
                                          : item.highlight
                                          ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                          : "text-navy-600 hover:bg-navy-50"
                                      }`}>
                                        <Icon className={`w-4 h-4 ${
                                          active ? "text-white" : item.highlight ? "text-emerald-600" : "text-navy-400"
                                        }`} />
                                        <div className="flex-1 min-w-0">
                                          <span className={`text-sm ${active ? "text-white font-medium" : ""}`}>
                                            {item.label}
                                          </span>
                                        </div>
                                        {item.badge && (
                                          <span className="min-w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
                                            {typeof item.badge === 'number' ? item.badge : ''}
                                          </span>
                                        )}
                                      </div>
                                    </Link>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              )}
            </nav>

            {/* Footer */}
            <div className="p-4 border-t border-navy-100">
              <button
                onClick={logout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium text-sm">Cerrar sesión</span>
              </button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
